import { useEffect, useState } from 'react';
import {
	REACT_APP_FIREBASE_APIKEY,
	REACT_APP_FIREBASE_AUTHDOMAIN,
	REACT_APP_FIREBASE_DATABASEURL,
	REACT_APP_FIREBASE_PROJECTID,
	REACT_APP_FIREBASE_STORAGEBUCKET,
	REACT_APP_FIREBASE_MESSAGINGSENDERID,
	REACT_APP_FIREBASE_AAPID,
	REACT_APP_RECAPTCHA_SITE_KEY,
} from 'envs';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getDatabase } from 'firebase/database';
import {
	getAuth,
	onAuthStateChanged,
	signInWithCustomToken,
} from 'firebase/auth';
import {
	initializeAppCheck,
	ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

const firebaseConfig = {
	apiKey: REACT_APP_FIREBASE_APIKEY,
	authDomain: REACT_APP_FIREBASE_AUTHDOMAIN,
	databaseURL: REACT_APP_FIREBASE_DATABASEURL,
	projectId: REACT_APP_FIREBASE_PROJECTID,
	storageBucket: REACT_APP_FIREBASE_STORAGEBUCKET,
	messagingSenderId: REACT_APP_FIREBASE_MESSAGINGSENDERID,
	appId: REACT_APP_FIREBASE_AAPID,
};

// Initialize once (guards against duplicate-app errors on re-render).
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const database = getDatabase(app);
const auth = getAuth(app);

// App Check — admin.panel is served only on our own domains, so reCAPTCHA
// Enterprise is used. Guarded by an env flag so it is a no-op until the
// reCAPTCHA Enterprise key + App Check provider are registered in the console.
if (REACT_APP_RECAPTCHA_SITE_KEY) {
	try {
		initializeAppCheck(app, {
			provider: new ReCaptchaEnterpriseProvider(REACT_APP_RECAPTCHA_SITE_KEY),
			isTokenAutoRefreshEnabled: true,
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to initialize Firebase App Check', error);
	}
}

let signInPromise: Promise<unknown> | null = null;

/**
 * Sign in to Firebase with a backend-minted custom token.
 *
 * RTDB reads/writes now require an authenticated user (`auth != null`), so this
 * must complete before any listener or lock transaction runs. Concurrent calls
 * are deduplicated and it is a no-op once a user is already signed in.
 */
export const signInToFirebase = (customToken?: string): Promise<unknown> => {
	if (!customToken || auth.currentUser) {
		return Promise.resolve();
	}
	if (!signInPromise) {
		signInPromise = signInWithCustomToken(auth, customToken).catch((error) => {
			signInPromise = null;
			// eslint-disable-next-line no-console
			console.error('Firebase signInWithCustomToken failed', error);
		});
	}
	return signInPromise;
};

/**
 * Returns the Realtime Database + Auth instances and `authReady`, which becomes
 * true once a Firebase user is signed in. Listener/lock effects should gate on
 * `authReady` (and include it in their dependency array) so they only run after
 * authentication, since RTDB now rejects unauthenticated access.
 */
export const useFirebaseInit = () => {
	const [authReady, setAuthReady] = useState<boolean>(!!auth.currentUser);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			setAuthReady(!!user);
		});
		return () => unsubscribe();
	}, []);

	return { database, auth, authReady };
};
